import { memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { Center, Flex, Grid, Text } from '@chakra-ui/react';

import type { FetchService } from '@izimi/api';
import { useIsMobile } from '@izimi/hooks';
import { getMainDocumentFromDeed } from '@izimi/modules';
import { isEmptyArray } from '@izimi/utils';

import { NabanDeedAdditions } from './components/documents/NabanDeedAdditions';
import { NabanDeedECBDocuments } from './components/documents/NabanDeedECBDocuments';
import { NabanDeedFinancesFeedbackDocuments } from './components/documents/NabanDeedFinancesFeedbackDocuments';
import { NabanDeedSentDocuments } from './components/documents/NabanDeedSentDocuments';
import { NabanDeedStatutesDocuments } from './components/documents/NabanDeedStatutesDocuments';
import { NabanDeedDetailsCard } from './components/NabanDeedDetailsCard';
import { NabanDeedLoading } from './components/NabanDeedLoading';
import { NabanDeedMainDocument } from './components/NabanDeedMainDocument';
import type { RenderDeedOptions } from './util/types';
import { ErrorPage } from '../../errors';
import { NabanIzimiAd } from '../components/NabanIzimiAd';
import { useNabanModule } from '../modules/useNabanModule';
import { useNabanStoreSelectedAuthenticationMethod } from '../store';

type Props = {
  fetchService: FetchService;
  showIzimiAd?: boolean;
  renderDeedOptions?: RenderDeedOptions;
  getPreviewRoute: (args: {
    deedId: string;
    documentIdentifier: string;
    isEmbedded: boolean;
  }) => string;
  onIzimiAdCtaButtonClick?: VoidFunction;
};

export const NabanDeed = memo(
  ({
    fetchService,
    showIzimiAd = true,
    renderDeedOptions,
    getPreviewRoute,
    onIzimiAdCtaButtonClick,
  }: Props) => {
    const { deedId } = useParams();
    const { useGetNabanDeed } = useNabanModule(fetchService);
    const {
      t,
      i18n: { language },
    } = useTranslation('naban');
    const isMobile = useIsMobile();
    const navigate = useNavigate();

    const selectedAuthenticationMethod = useNabanStoreSelectedAuthenticationMethod();

    const { deed, isLoading, error } = useGetNabanDeed({
      napId: deedId,
      authenticationModeToken: selectedAuthenticationMethod?.token,
      language,
    });

    const mainDocument = getMainDocumentFromDeed({ deed });

    const mainDocumentTitle = useMemo(() => {
      if (mainDocument == null) {
        return '';
      }

      if ('title' in mainDocument) {
        return mainDocument.title;
      }

      return mainDocument.documentType?.label ?? '';
    }, [mainDocument]);

    const documentIdentifier = mainDocument?.documentIdentifier;

    const onMainCardPressed =
      deedId && documentIdentifier
        ? () => {
            navigate(getPreviewRoute({ deedId, documentIdentifier, isEmbedded: false }));
          }
        : undefined;

    if (isLoading) {
      return <NabanDeedLoading />;
    }

    if (error || deed == null) {
      return (
        <Center alignItems='center' flex={1}>
          <ErrorPage title={t('deed.error.title')} message={t('deed.error.description')} />
        </Center>
      );
    }

    return (
      <Flex flexDirection='column' gap={4} flex={1}>
        <Text variant='heading1' mb={{ base: 2, lg: 4 }}>
          {deed.deedDetails.deedType.label}
        </Text>
        <Grid
          gridTemplateColumns={{ base: '1fr', lg: '3fr 1fr' }}
          gap={{ base: 4, md: 8 }}
          alignItems='start'>
          <Flex flexDirection='column' gap={{ base: 4, md: 8 }} minW={0}>
            <NabanDeedMainDocument
              title={t('naban:deed.mainDocument.title')}
              onClick={onMainCardPressed}>
              {renderDeedOptions
                ? renderDeedOptions({
                    documentToken: mainDocument?.documentToken,
                    documentTitle: mainDocumentTitle ?? '',
                    napId: deed.deedDetails.napId,
                    authenticationModeToken: selectedAuthenticationMethod?.token,
                    documentIdentifier: mainDocument?.documentIdentifier,
                    isEmbeddedDocument: false,
                  })
                : null}
            </NabanDeedMainDocument>
            {(!isEmptyArray(deed.financeDocuments) || !isEmptyArray(deed.justiceDocuments)) && (
              <NabanDeedSentDocuments
                justiceDocuments={deed.justiceDocuments}
                financeDocuments={deed.financeDocuments}
                renderDeedOptions={renderDeedOptions}
                napId={deed.deedDetails.napId}
              />
            )}
            {!isEmptyArray(deed.financeFeedbackDocuments) && (
              <NabanDeedFinancesFeedbackDocuments
                documents={deed.financeFeedbackDocuments}
                renderDeedOptions={renderDeedOptions}
                napId={deed.deedDetails.napId}
              />
            )}
            {deed.bceEnterpriseNumberFeedback && (
              <NabanDeedECBDocuments document={deed.bceEnterpriseNumberFeedback} />
            )}
            {deed.statutesDocument && (
              <NabanDeedStatutesDocuments
                document={deed.statutesDocument}
                renderDeedOptions={renderDeedOptions}
                napId={deed.deedDetails.napId}
              />
            )}
            {!isEmptyArray(deed.additions) && (
              <NabanDeedAdditions
                renderDeedOptions={renderDeedOptions}
                napId={deed.deedDetails.napId}
                documents={deed.additions}
              />
            )}
            <NabanDeedDetailsCard
              deedDetails={deed.deedDetails}
              display={{ base: 'flex', lg: 'none' }}
            />
            {showIzimiAd && (
              <NabanIzimiAd vertical={isMobile} onIzimiAdCtaButtonClick={onIzimiAdCtaButtonClick} />
            )}
          </Flex>
          <NabanDeedDetailsCard
            deedDetails={deed.deedDetails}
            display={{ base: 'none', lg: 'flex' }}
          />
        </Grid>
      </Flex>
    );
  },
);

NabanDeed.displayName = 'MemoNabanDeed';
